import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Loader, Typography } from '@thinknet-gmbh/platform-common-ui';
import { formatCurrency, PlatformCurrenciesEnum } from '@thinknet-gmbh/platform-common';

import { useSubscribe } from 'src/hooks/subscriptions/subscribe.hook';
import { useGetSoftwarePlanById } from 'src/hooks/software-plan/get-software-plan-by-id.hook';
import { ISubscribeInfo } from 'src/components/software-plans-modal/software-plans-modal.interfaces';
import { StripeCardForm } from 'src/components/stripe-card-form';
import { useHandlePaymentChanges } from 'src/hooks/subscriptions/subscribe-to-payment-intent-changes.hook';
import { useChangeContinueWorkingStatus } from 'src/hooks/accounts/change-continue-working-status.hook';
import { TotalWrapper, FormWrapper } from './payment-card-form.styles';

interface IProps {
  onClose: () => void;
  subscribeInfo: ISubscribeInfo;
}

const PaymentCardForm: FC<IProps> = ({ subscribeInfo, onClose }) => {
  const { t } = useTranslation(['common', 'products', 'dashboard']);
  const { softwarePlanId, additionalFeatureIds, price } = subscribeInfo;
  const { softwarePlan } = useGetSoftwarePlanById(softwarePlanId);
  const { additionalFeatures = [] } = softwarePlan || {};

  const { isShowLoading } = useHandlePaymentChanges({ onPaymentFinished: onClose });
  const { subscribe, loading } = useSubscribe();

  const { changeContinueWorkingStatus } = useChangeContinueWorkingStatus();

  const selectedAdditionalFeatures = additionalFeatures.reduce(
    (memo: any, { _id, name }: any) =>
      additionalFeatureIds.includes(_id) ? [...memo, name] : memo,
    []
  );

  return (
    <FormWrapper>
      {(loading || isShowLoading) && <Loader position="absolute" />}
      <TotalWrapper>
        <Typography>{t(`products:labels.selectedSoftwarePlan`)}:</Typography>
        <Typography weight="bold" title={softwarePlan.name}>
          {softwarePlan.name}
        </Typography>
      </TotalWrapper>
      {selectedAdditionalFeatures.length ? (
        <Typography weight="bold">+ {selectedAdditionalFeatures.join(', ')}</Typography>
      ) : null}
      <TotalWrapper space>
        <Typography>{t(`dashboard:labels.totalPrice`)}:</Typography>
        <Typography weight="bold">{formatCurrency(price, PlatformCurrenciesEnum.USD)}</Typography>
      </TotalWrapper>
      <StripeCardForm
        onClose={onClose}
        onFinish={paymentMethodId =>
          subscribe({
            ...subscribeInfo,
            stripePaymentMethodId: paymentMethodId,
          }).then(response => {
            changeContinueWorkingStatus({
              variables: {
                payload: {
                  canContinueWorking: !response.errors,
                },
              },
            });
          })
        }
      />
    </FormWrapper>
  );
};

export default PaymentCardForm;
